import { z } from 'zod'
import { acreageSchema } from '../validators/decimal-string.validator.js'
import { uuidSchema } from '../validators/uuid.validator.js'
import { siteMapStatusSchema } from '../enums/site-map-status.enum.js'
import { siteBoundarySchema } from './geo.dto.js'

/**
 * A place where work happens. A client can have many — an HOA spanning several
 * parks is one client with one site per park — which is why this is its own
 * record rather than an address column on the client.
 *
 * `mapStatus` tracks whether we have drawn this site's map yet; that happens on
 * its own timeline, before or after the work is won. A site with a boundary is
 * always COMPLETE; the stored, hand-picked value only answers for one without.
 */
export const siteDtoSchema = z.object({
  id: uuidSchema,
  clientId: uuidSchema,
  /** What the site is called, e.g. "Adrianne K Hamilton Park". */
  name: z.string(),

  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postalCode: z.string().nullable(),

  mapStatus: siteMapStatusSchema,
  /**
   * The drawn outline of the site, or null if nobody has traced one.
   *
   * Structural only — this schema does NOT reject a self-intersecting ring, and
   * the input schema does. Strict on the way in, tolerant on the way out: a row
   * that predates a rule, or was written by some other path, should still render
   * so a person can go and fix it, rather than failing the whole site payload
   * and leaving them no screen on which to do so.
   */
  boundary: siteBoundarySchema.nullable(),
  /**
   * Acres, COMPUTED from `boundary` — never stored, never accepted as input.
   *
   * Null when nothing has been drawn, which is the honest answer and doubles as
   * the signal that the site has not been mapped yet. It stays on the DTO so
   * every reader gets the same figure from one calculation rather than each
   * deriving its own from the vertices.
   *
   * A string, like every other exact quantity here, so the two places it is
   * shown cannot disagree in the last digit.
   */
  acreage: acreageSchema.nullable(),
  notes: z.string().nullable(),
  /**
   * The address on the client's Housecall Pro customer that this site is,
   * as HCP had it when linked. Null until linked; a site's jobs go here.
   */
  hcpAddress: z.object({ id: z.string(), label: z.string() }).nullable(),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

export type SiteDto = z.infer<typeof siteDtoSchema>
